/**
 * LifecycleSectionForm
 *
 * Generic, self-contained editable section used by the Edit Device page to let
 * an operator correct the data captured at a single lifecycle step (procurement,
 * device keys, provisioning, fault, retirement). Each rendered instance is one
 * card with its own Save button that persists via a dedicated upsert endpoint —
 * the fetch-merge-update and endpoint wiring live in the parent
 * (EditDeviceLifecycleSections), so this component only handles rendering + local
 * form state.
 *
 * Editing here is data correction, NOT a lifecycle transition — the parent's
 * save payloads deliberately omit every `deviceStatus` / `*Status` field.
 */

import * as React from 'react'
import { z } from 'zod'
import type { LucideIcon } from 'lucide-react'
import { useAppForm } from '@/hooks/app-form'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import {
  renderControl,
  renderButtonControl,
  type ControlConfig,
} from '@/forms/formRenderFunctions'

export type LifecycleFieldControl = 'text' | 'number' | 'date' | 'select' | 'checkbox' | 'textarea'

export interface LifecycleSectionField {
  name: string
  label: string
  control: LifecycleFieldControl
  placeholder?: string
  options?: Array<{ value: string; label: string }>
  isLoading?: boolean
  description?: string
}

export interface LifecycleSectionFormProps {
  title: string
  description?: string
  icon: LucideIcon
  fields: LifecycleSectionField[]
  /** Prefilled values (dates as ISO strings, numbers as strings, booleans as booleans). */
  initialValues: Record<string, unknown>
  /** Persists the edited values; the parent wraps them into the endpoint payload. */
  onSave: (values: Record<string, unknown>) => Promise<void>
  isSaving: boolean
  saveLabel?: string
}

// Edit sections are for correcting captured data, so every field is optional —
// requiredness (which enforces a lifecycle transition) does not apply here.
function buildSectionSchema(fields: LifecycleSectionField[]) {
  const shape: Record<string, z.ZodTypeAny> = {}
  for (const f of fields) shape[f.name] = z.any().optional()
  return z.object(shape)
}

function toControlConfig(field: LifecycleSectionField): ControlConfig {
  return {
    name: field.name,
    label: field.label,
    placeholder: field.placeholder,
    description: field.description,
    visibility: true,
    options: field.options,
    isLoading: field.isLoading,
    htmlControl:
      field.control === 'checkbox'
        ? 'checkbox'
        : field.control === 'textarea'
          ? 'textarea'
          : field.control === 'select'
            ? 'select'
            : undefined,
    type: field.control === 'date' ? 'date' : field.control === 'number' ? 'number' : 'text',
  }
}

export function LifecycleSectionForm(props: LifecycleSectionFormProps) {
  const { title, description, icon: Icon, fields, initialValues, onSave, isSaving, saveLabel } = props

  const schema = React.useMemo(() => buildSectionSchema(fields), [fields])

  const form = useAppForm({
    schema,
    defaultValues: initialValues,
    onSubmit: async ({ value }) => {
      await onSave(value as Record<string, unknown>)
    },
  })

  return (
    <form.AppForm>
      <Card className="shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-primary" />
            <CardTitle className="text-base">{title}</CardTitle>
          </div>
          {description ? <CardDescription>{description}</CardDescription> : null}
        </CardHeader>
        <CardContent className="space-y-4">
          {fields.map((field) => (
            <form.AppField key={field.name} name={field.name as never}>
              {(bound: unknown) =>
                renderControl(toControlConfig(field), bound as never)
              }
            </form.AppField>
          ))}

          <div className="pt-2 flex justify-end">
            {renderButtonControl(
              {
                label: isSaving ? 'Saving...' : (saveLabel ?? 'Save'),
                variant: 'default',
                type: 'submit',
              },
              { SubmitButton: form.SubmitButton }
            )}
          </div>
        </CardContent>
      </Card>
    </form.AppForm>
  )
}
